import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input as SInput } from 'semantic-ui-react';
import { CustomSchemaTypes, SchemaTypes } from '@axiom/types';
import debounce from 'lodash/debounce';

import { DesignSysEnvUtil } from '../../../utils/design-sys-env-util';

const SchemaTypeMap = {
  [CustomSchemaTypes.SchemaEmail]: 'email',
  [CustomSchemaTypes.SchemaCurrency]: 'text',
  [SchemaTypes.ZodNumber]: 'number',
  [CustomSchemaTypes.SchemaPassword]: 'password',
  [SchemaTypes.ZodString]: 'text',
  [SchemaTypes.ZodEnum]: 'text',
  [CustomSchemaTypes.SchemaPhoneNumber]: 'text',
  [CustomSchemaTypes.SchemaRate]: 'text',
} as const;
const SCHEMA_TYPE_NAMES = Object.keys(SchemaTypeMap);

export type RawInputValueType = string | number | null;

export type RawInputProps = {
  disabled?: boolean;
  id?: string;
  invalid?: boolean;
  name: string;
  onBlur: (
    value: RawInputValueType,
    e: React.FocusEvent<HTMLInputElement>
  ) => void;
  onChange: (value: RawInputValueType) => Promise<unknown> | void;
  onFocus: (
    value: RawInputValueType,
    e: React.FocusEvent<HTMLInputElement>
  ) => void;
  placeholder?: string;
  type: keyof typeof SchemaTypeMap;
  value?: RawInputValueType;
};

const formatOut = ({
  type,
  value,
}: {
  type: string;
  value: string;
}): RawInputValueType => {
  let formatValue: RawInputValueType = value;
  if (formatValue === '') {
    formatValue = null;
  }

  if (type === SchemaTypes.ZodNumber && formatValue !== null) {
    formatValue = +formatValue;
  }

  return formatValue;
};

const formatIn = (value?: RawInputValueType) => {
  let formattedValue = value;
  if (formattedValue === null || formattedValue === undefined) {
    formattedValue = '';
  }

  return formattedValue;
};

export const RawInput = ({
  disabled = false,
  id,
  invalid = false,
  name,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  type,
  value,
}: RawInputProps) => {
  if (!SCHEMA_TYPE_NAMES.includes(type)) {
    throw new Error(
      `Unknown Input Schema Type: ${type}; Allowed types: ${SCHEMA_TYPE_NAMES.join(
        ', '
      )}`
    );
  }

  /**
   * Keeps it from flop-toggling between an uncontrolled/controlled component
   */

  const formattedValue = formatIn(value);
  const [inputState, setInputState] =
    useState<RawInputValueType>(formattedValue);

  // Refs keep debounceChange (memoized with []) from calling a stale onChange/type after props change.
  const onChangeRef = useRef(onChange);
  const typeRef = useRef(type);
  useEffect(() => {
    onChangeRef.current = onChange;
    typeRef.current = type;
  });

  /*
    The typed value is captured here and not re-read when this fires: reading the input back at
    flush time reported whatever the `value` prop had put there in the meantime, so a form
    re-seeding mid-word replaced the keystrokes instead of reporting them.
  */
  const debounceChange = useMemo(
    () =>
      debounce((rawValue: string) => {
        void onChangeRef.current(
          formatOut({ type: typeRef.current, value: rawValue })
        );
      }, DesignSysEnvUtil.typingDebounce),
    []
  );
  useEffect(() => {
    if (formattedValue !== inputState) {
      /*
        The prop wins, so a keystroke still waiting is abandoned rather than reported after it -
        which would leave the caller holding a value the field has stopped showing.
      */
      debounceChange.cancel();
      setInputState(formattedValue);
    }
  }, [value]);

  // Nothing left pending: the callback writes into a form that went with this field.
  useEffect(
    () => () => {
      debounceChange.cancel();
    },
    [debounceChange]
  );

  return (
    <div data-test={name}>
      <SInput
        fluid
        key={name}
        disabled={disabled}
        error={invalid}
        id={id}
        name={name}
        onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
          onBlur(formatOut({ type, value: event.target.value }), event);
        }}
        onChange={event => {
          const rawValue = event.target.value;

          setInputState(formatOut({ type, value: rawValue }));
          if (DesignSysEnvUtil.typingDebounce > 0) {
            debounceChange(rawValue);
          } else {
            void onChange(formatOut({ type, value: rawValue }));
          }
        }}
        onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
          onFocus(formatOut({ type, value: event.target.value }), event);
        }}
        placeholder={placeholder}
        type={SchemaTypeMap[type]}
        value={inputState ?? ''}
      />
    </div>
  );
};
